/**
 * 위치 앵커 React 훅 (#365)
 *
 * `locationAnchor.ts` 의 sessionStorage 기반 앵커를 React 컴포넌트에서
 * 안전하게 구독하기 위한 SSR-safe 훅. 메뉴 목록 페이지
 * (BrandDetailPage / OrganizationDetailPage) 에서 source 컨텍스트
 * (qr-table vs qr-pickup) 분기 표시에 사용한다.
 *
 * 박제 D — 단일 출처:
 * - 앵커의 정합성은 `locationAnchor.ts` 의 `getLocationAnchor()` 가 단일 출처.
 *   본 훅은 단순 React 래퍼이며, 스키마/TTL 검증을 중복하지 않는다.
 *
 * 동기화 정책:
 * - mount 시 1회 + `storage` / `pageshow` / `focus` 이벤트 시 재조회.
 * - 같은 탭 내 set/clear 는 같은 페이지 사이클에서 navigate 가 동반되어
 *   useEffect 가 자연 재평가되므로 polling 도입하지 않음 (Karpathy: 최소 표면).
 */

import { useEffect, useState } from 'react';
import { getLocationAnchor, type LocationAnchor } from '@/lib/locationAnchor';

export function useLocationAnchor(): LocationAnchor | null {
  const [anchor, setAnchor] = useState<LocationAnchor | null>(() =>
    typeof window === 'undefined' ? null : getLocationAnchor()
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sync = (): void => {
      setAnchor(getLocationAnchor());
    };

    sync();

    window.addEventListener('storage', sync);
    window.addEventListener('pageshow', sync);
    window.addEventListener('focus', sync);

    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('pageshow', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  return anchor;
}
